import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V1, authFetch } from '../constants';
import { useEntities } from '../hooks/useEntities';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import ActionButton from '../components/ui/ActionButton';

interface StepDef {
  label: string;
  description: string;
}

const STEPS: StepDef[] = [
  { label: 'Organization', description: 'Review your organization details' },
  { label: 'Legal Entities', description: 'Set up legal entities for your organization' },
  { label: 'Chart of Accounts', description: 'Upload or configure your chart of accounts' },
  { label: 'Users', description: 'Create initial users and assign roles' },
  { label: 'Policies', description: 'Configure client policies and thresholds' },
  { label: 'Data Import', description: 'Import initial GL data' },
  { label: 'Review', description: 'Review configuration and complete onboarding' },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const entities = useEntities();
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState<boolean[]>(new Array(STEPS.length).fill(false));
  const [orgInfo, setOrgInfo] = useState<any>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Entity creation modal
  const [showAddEntity, setShowAddEntity] = useState(false);
  // Phase 9.6 -- 'functional_currency: ""' instead of hardcoded 'USD' so
  // the country onChange handler can auto-fill it from /reference.
  // 'aiOnboarding' toggle drives whether we POST to /entities (basic
  // create, legacy) or /entities/onboarding (Phase 9.5 wizard endpoint
  // -- seeds CoA template + default Operating bank/sub-account).
  const [entityForm, setEntityForm] = useState({
    entity_code: '', entity_name: '', country: '',
    functional_currency: '', reporting_standard: 'US_GAAP',
  });
  const [aiOnboarding, setAiOnboarding] = useState(true);

  // Phase 9.5 -- country -> currency reference catalog
  const [countries, setCountries] = useState<Array<{
    country_iso: string; country_name: string;
    currency_code: string; currency_name: string;
  }>>([]);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  useEffect(() => {
    authFetch(`${API_V1}/organization`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setOrgInfo(d); })
      .catch(() => {});
    // Phase 9.6 -- preload country/currency catalog for the dropdown.
    authFetch(`${API_V1}/reference/countries`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.countries) setCountries(d.countries); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const c = [...completed];
    if (orgInfo) c[0] = true;
    if (entities.length > 0) c[1] = true;
    setCompleted(c);
  }, [orgInfo, entities]); // eslint-disable-line

  const markCompleted = (idx: number) => {
    const c = [...completed];
    c[idx] = true;
    setCompleted(c);
  };

  const handleCreateEntity = async () => {
    try {
      // Phase 9.5/9.6 -- /onboarding endpoint seeds CoA template +
      // default Operating bank/sub-account in the same transaction.
      // Legacy /entities POST stays available for callers that want
      // just the bare entity_registry row.
      const url = aiOnboarding
        ? `${API_V1}/entities/onboarding`
        : `${API_V1}/entities`;
      const body = aiOnboarding
        ? {
            entity_code: entityForm.entity_code,
            entity_name: entityForm.entity_name,
            country: entityForm.country,
            functional_currency: entityForm.functional_currency || undefined,
            reporting_framework: entityForm.reporting_standard,
            local_framework: entityForm.reporting_standard,
            create_default_bank: true,
            seed_coa_template: true,
          }
        : entityForm;
      const res = await authFetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const d = await res.json();
        let msg = 'Entity created';
        if (d.steps_completed?.length) {
          const coa = d.coa_template_accounts_created || 0;
          const sub = d.default_bank_sub_account_code;
          const parts = [`Entity ${d.entity_code}`, `${d.functional_currency}`];
          if (coa) parts.push(`${coa} CoA accounts seeded`);
          if (sub) parts.push(`bank ${sub}`);
          msg = parts.join(' / ');
        }
        showToast(msg, true);
        setShowAddEntity(false);
        setEntityForm({ entity_code: '', entity_name: '', country: '', functional_currency: '', reporting_standard: 'US_GAAP' });
      } else {
        const e = await res.json();
        showToast(e.detail || 'Error creating entity', false);
      }
    } catch { showToast('Failed', false); }
  };

  // Phase 9.6 -- country change auto-fills functional_currency from the
  // D133 catalog. Operator can still override via the currency input
  // below (rare cross-country-entity case: e.g. a Brazilian sub of a
  // German parent that reports in EUR rather than BRL).
  const handleCountryChange = (iso: string) => {
    const match = countries.find(c => c.country_iso === iso);
    setEntityForm({
      ...entityForm,
      country: iso,
      functional_currency: match?.currency_code || entityForm.functional_currency,
    });
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-subtle)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };
  const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 24, border: '1px solid var(--border-subtle)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>Organization Details</h3>
            {orgInfo ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={labelStyle}>Name</div>
                  <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>{orgInfo.name || orgInfo.org_name || 'N/A'}</div>
                </div>
                <div>
                  <div style={labelStyle}>Slug</div>
                  <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>{orgInfo.slug || orgInfo.org_slug || 'N/A'}</div>
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading organization info...</div>
            )}
            <div style={{ marginTop: 16 }}>
              <ActionButton label="Confirm Organization" onClick={() => markCompleted(0)} />
            </div>
          </div>
        );

      case 1:
        return (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 24, border: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Legal Entities ({entities.length})</h3>
              <ActionButton label="+ Add Entity" onClick={() => setShowAddEntity(true)} size="sm" />
            </div>
            {entities.length > 0 ? (
              <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Code', 'Name', 'Country', 'Currency'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entities.map(e => (
                      <tr key={e.entity_code} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{e.entity_code}</td>
                        <td style={tdStyle}>{e.entity_name}</td>
                        <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{e.country}</td>
                        <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{e.functional_currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: 'var(--text-hint)', fontSize: 13, padding: 20, textAlign: 'center' }}>No entities configured yet. Add your first entity above.</div>
            )}
            {entities.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <ActionButton label="Confirm Entities" onClick={() => markCompleted(1)} />
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 24, border: '1px solid var(--border-subtle)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>Chart of Accounts</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Upload your Chart of Accounts CSV through Data Management or configure it manually.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <ActionButton label="Go to Data Management" onClick={() => navigate('/data-management')} />
              <ActionButton label="Skip (Already Configured)" onClick={() => markCompleted(2)} variant="secondary" />
            </div>
          </div>
        );

      case 3:
        return (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 24, border: '1px solid var(--border-subtle)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>Users</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Create initial users with appropriate roles and access levels.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <ActionButton label="Go to User Management" onClick={() => navigate('/user-management')} />
              <ActionButton label="Skip" onClick={() => markCompleted(3)} variant="secondary" />
            </div>
          </div>
        );

      case 4:
        return (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 24, border: '1px solid var(--border-subtle)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>Client Policies</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Configure detection thresholds, approval policies, and compliance settings.
            </p>
            <div style={{ background: 'var(--bg-elevated)', borderRadius: 6, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Default Policies Active:</div>
              <ul style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, paddingLeft: 20 }}>
                <li>Auto-approval threshold: $50,000</li>
                <li>Materiality threshold: 5% of account balance</li>
                <li>Duplicate detection: Enabled</li>
                <li>SOX compliance: Enabled</li>
              </ul>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <ActionButton label="Go to Configuration" onClick={() => navigate('/configuration')} />
              <ActionButton label="Accept Defaults" onClick={() => markCompleted(4)} variant="secondary" />
            </div>
          </div>
        );

      case 5:
        return (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 24, border: '1px solid var(--border-subtle)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>Data Import</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Upload your initial GL trial balance data to begin processing.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <ActionButton label="Go to Data Management" onClick={() => navigate('/data-management')} />
              <ActionButton label="Skip (Import Later)" onClick={() => markCompleted(5)} variant="secondary" />
            </div>
          </div>
        );

      case 6:
        return (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 24, border: '1px solid var(--border-subtle)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>Review & Complete</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              {STEPS.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: completed[i] ? '#dcfce7' : 'var(--bg-elevated)', borderRadius: 6 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: completed[i] ? '#166534' : 'var(--border-default)', color: completed[i] ? '#fff' : 'var(--text-secondary)' }}>
                    {completed[i] ? '\u2713' : i + 1}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: completed[i] ? '#166534' : 'var(--text-secondary)' }}>{completed[i] ? 'Completed' : 'Pending'}</div>
                  </div>
                </div>
              ))}
            </div>
            <ActionButton
              label="Complete Onboarding"
              onClick={() => { markCompleted(6); showToast('Onboarding complete!', true); setTimeout(() => navigate('/'), 1500); }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <PageLayout title="Client Onboarding" subtitle="Step-by-step wizard to configure a new client">
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000, padding: '10px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive)' : 'var(--negative)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
          {toast.msg}
        </div>
      )}

      {/* Stepper */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, padding: '0 20px' }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 0 }}>
            <div
              onClick={() => setStep(i)}
              style={{
                width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s',
                background: completed[i] ? '#166534' : i === step ? 'var(--accent)' : 'var(--bg-elevated)',
                color: completed[i] || i === step ? '#fff' : 'var(--text-secondary)',
                border: i === step ? '2px solid var(--accent)' : '2px solid transparent',
              }}
              title={s.label}
            >
              {completed[i] ? '\u2713' : i + 1}
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: completed[i] ? '#166534' : 'var(--border-subtle)', margin: '0 4px', transition: 'background 0.2s' }} />
            )}
          </div>
        ))}
      </div>

      {/* Step label */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-hint)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Step {step + 1} of {STEPS.length}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginTop: 4 }}>{STEPS[step].label}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{STEPS[step].description}</div>
      </div>

      {/* Step content */}
      {renderStepContent()}

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <ActionButton
          label="Previous"
          onClick={() => setStep(Math.max(0, step - 1))}
          variant="secondary"
          disabled={step === 0}
        />
        <ActionButton
          label="Next"
          onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))}
          disabled={step === STEPS.length - 1}
        />
      </div>

      {/* Add Entity Modal */}
      <Modal open={showAddEntity} onClose={() => setShowAddEntity(false)} title="Add Legal Entity" size="md"
        footer={
          <>
            <ActionButton label="Cancel" onClick={() => setShowAddEntity(false)} variant="secondary" />
            <ActionButton label="Create Entity" onClick={handleCreateEntity} />
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Phase 9.6 -- AI-native onboarding toggle. When on, the
              backend seeds the CoA template + default Operating bank
              + per-bank GL sub-account in one transaction. */}
          <div style={{
            padding: '10px 12px', borderRadius: 6,
            background: aiOnboarding ? 'rgba(34,197,94,0.08)' : 'var(--bg-subtle)',
            border: `1px solid ${aiOnboarding ? '#22c55e' : 'var(--border-subtle)'}`,
            fontSize: 12, color: 'var(--text-secondary)',
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <input
              type="checkbox"
              aria-label="Enable AI-native onboarding (seed CoA template + default bank)"
              checked={aiOnboarding}
              onChange={e => setAiOnboarding(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <div>
              <strong style={{ color: 'var(--text-primary)' }}>AI-native onboarding</strong>
              {' '}- when enabled, the platform also seeds ~40 standard CoA
              accounts and creates a default Operating bank account with
              its per-bank GL sub-account (Phase 7a SAP/NetSuite convention).
            </div>
          </div>
          <div><label style={labelStyle}>Entity Code</label><input style={inputStyle} value={entityForm.entity_code} onChange={e => setEntityForm({ ...entityForm, entity_code: e.target.value })} placeholder="e.g. 1000, USA, BRA" /></div>
          <div><label style={labelStyle}>Entity Name</label><input style={inputStyle} value={entityForm.entity_name} onChange={e => setEntityForm({ ...entityForm, entity_name: e.target.value })} /></div>
          <div>
            <label style={labelStyle}>Country</label>
            {countries.length > 0 ? (
              <select
                aria-label="Country"
                style={inputStyle}
                value={entityForm.country}
                onChange={e => handleCountryChange(e.target.value)}
              >
                <option value="">-- Select country --</option>
                {countries.map(c => (
                  <option key={c.country_iso} value={c.country_iso}>
                    {c.country_name} ({c.country_iso}) - {c.currency_code}
                  </option>
                ))}
              </select>
            ) : (
              <input
                style={inputStyle}
                value={entityForm.country}
                onChange={e => setEntityForm({ ...entityForm, country: e.target.value })}
                placeholder="e.g. US, Saudi Arabia"
              />
            )}
          </div>
          <div>
            <label style={labelStyle}>
              Functional Currency
              {entityForm.country && entityForm.functional_currency && (
                <span style={{
                  marginLeft: 8, fontSize: 10, padding: '1px 6px',
                  borderRadius: 4, background: 'rgba(34,197,94,0.12)',
                  color: '#22c55e', textTransform: 'none',
                }}>
                  auto-filled from country
                </span>
              )}
            </label>
            <input
              style={inputStyle}
              value={entityForm.functional_currency}
              onChange={e => setEntityForm({ ...entityForm, functional_currency: e.target.value.toUpperCase() })}
              placeholder="USD, SAR, BRL, EUR..."
              maxLength={3}
            />
          </div>
          <div>
            <label style={labelStyle}>Reporting Standard</label>
            <select style={inputStyle} value={entityForm.reporting_standard} onChange={e => setEntityForm({ ...entityForm, reporting_standard: e.target.value })}>
              <option value="US_GAAP">US GAAP</option>
              <option value="IFRS">IFRS</option>
            </select>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px', textAlign: 'left', fontSize: 11,
  fontWeight: 600, color: 'var(--text-hint)',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '2px solid var(--border-subtle)',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)',
};
